"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Trophy } from "lucide-react";
import { BlockiesAvatar } from "@/components/BlockiesAvatar";
import { useGetCampaignUser } from "@/hooks/challenges";
import { CAMPAIGN_ID, CAMPAIGN_GROUP_ID } from "@/config/campaign";
import { useConnection } from "@/contexts/connection-context";
import {
  useLeaderboard,
  getDisplayName,
  type LeaderboardUser,
} from "@/hooks/useLeaderboard";

const LeaderboardItem = ({
  user,
  isCurrentUser = false,
}: {
  user: LeaderboardUser;
  isCurrentUser?: boolean;
}) => {
  const displayName = getDisplayName(user);

  return (
    <div
      className={`grid grid-cols-12 gap-4 p-4 rounded-xl transition-all border ${
        isCurrentUser
          ? "bg-[#60FF8A] text-black border-[#60FF8A]/50"
          : "bg-[#0C1113] hover:bg-[#0C1113]/80 text-white border-gray-800/50"
      }`}
    >
      {/* Rank */}
      <div className="col-span-2 flex items-center gap-2">
        <span
          className={`text-lg font-bold ${
            isCurrentUser ? "text-black" : "text-gray-400"
          }`}
        >
          #{user.rank}
        </span>
      </div>

      {/* Address */}
      <div className="col-span-6 flex items-center gap-3">
        <div className="w-8 h-8 rounded-full overflow-hidden">
          <BlockiesAvatar
            seed={user.address || displayName}
            size={8}
            scale={4}
          />
        </div>
        <span
          className={`font-mono text-sm ${
            isCurrentUser ? "text-black" : "text-white"
          }`}
        >
          {displayName}
        </span>
      </div>

      {/* Points */}
      <div className="col-span-4 flex items-center justify-end gap-2">
        <span
          className={`font-bold text-lg ${
            isCurrentUser ? "text-black" : "text-white"
          }`}
        >
          {Math.floor(user.points).toLocaleString()}
        </span>
        <span className={`text-sm ${isCurrentUser ? "text-black/70" : "text-gray-400"}`}>
          RP
        </span>
      </div>
    </div>
  );
};

export default function LeaderboardPage() {
  const { isAuthenticated, walletAddress } = useConnection();
  const [currentPage, setCurrentPage] = useState(0);
  const limit = 10;

  // Fetch leaderboard data using shared hook
  const { leaderboard, isLoading, hasNextPage, hasPrevPage } = useLeaderboard(
    walletAddress || "",
    limit,
    currentPage
  );

  // Fetch current user data
  const { data: currentUser } = useGetCampaignUser(
    CAMPAIGN_ID,
    CAMPAIGN_GROUP_ID,
    isAuthenticated
  );

  const handleNextPage = () => {
    if (hasNextPage) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (hasPrevPage) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0C1113] p-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <div className="h-20 bg-[#0C1113] border border-gray-800/50 rounded-xl animate-pulse" />
          <div className="space-y-3">
            {[...Array(10)].map((_, i) => (
              <div
                key={i}
                className="h-16 bg-[#0C1113] border border-gray-800/50 rounded-xl animate-pulse"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0C1113] p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="text-center"
        >
          <h1 className="text-4xl font-bold text-[#60FF8A] mb-2">
            Leaderboard
          </h1>
          <p className="text-gray-400 text-lg">
            Compete with others and climb the ranks
          </p>
        </motion.div>

        {/* Current User Stats */}
        {isAuthenticated && currentUser && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="bg-[#60FF8A] rounded-2xl p-6 text-black border border-[#60FF8A]/50"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-black/70 text-sm mb-1">Your Position</p>
                <p className="text-3xl font-bold text-black">#{currentUser.rank || "—"}</p>
              </div>
              <div className="text-right">
                <p className="text-black/70 text-sm mb-1">Your Points</p>
                <div className="flex items-center gap-2 justify-end">
                  <p className="text-3xl font-bold text-black">
                    {Math.floor(
                      Number(currentUser.totalPoints) || 0
                    ).toLocaleString()}
                  </p>
                  <span className="text-sm text-black">RP</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Leaderboard Table Header */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="grid grid-cols-12 gap-4 px-4 py-2"
        >
          <div className="col-span-2 text-sm font-semibold text-gray-400 uppercase tracking-wider">
            Rank
          </div>
          <div className="col-span-6 text-sm font-semibold text-gray-400 uppercase tracking-wider">
            Address
          </div>
          <div className="col-span-4 text-sm font-semibold text-gray-400 uppercase tracking-wider text-right">
            Points
          </div>
        </motion.div>

        {/* Leaderboard List */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          className="space-y-2"
        >
          {leaderboard.map((user: LeaderboardUser, index: number) => {
            const isCurrentUser = user.address === walletAddress;

            return (
              <motion.div
                key={`${user.address || user.rank}`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2, delay: index * 0.03 }}
              >
                <LeaderboardItem user={user} isCurrentUser={isCurrentUser} />
              </motion.div>
            );
          })}

          {leaderboard.length === 0 && (
            <div className="text-center py-12 text-gray-400">
              <Trophy className="w-16 h-16 mx-auto mb-4 text-gray-600" />
              <p className="text-lg">No leaderboard data available</p>
              <p className="text-sm text-gray-500 mt-2">
                Complete quests to appear on the leaderboard!
              </p>
            </div>
          )}
        </motion.div>

        {/* Pagination Controls */}
        {(hasPrevPage || hasNextPage) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.4 }}
            className="flex items-center justify-between pt-4"
          >
            <motion.button
              onClick={handlePrevPage}
              disabled={!hasPrevPage}
              whileHover={hasPrevPage ? { scale: 1.05, y: -2 } : {}}
              whileTap={hasPrevPage ? { scale: 0.95 } : {}}
              className={`relative flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                hasPrevPage
                  ? "bg-[#0C1113] border border-gray-800/50 text-white hover:bg-[#0C1113]/80 hover:border-gray-700/50 hover:shadow-lg"
                  : "bg-gray-800 text-gray-500 cursor-not-allowed"
              }`}
            >
              <span className="absolute inset-0 bg-white opacity-0 group-hover:opacity-5 transition-opacity duration-200 rounded-lg" />
              <ChevronLeft className="w-4 h-4" />
              Previous
            </motion.button>

            <span className="text-sm text-gray-400">Page {currentPage + 1}</span>

            <motion.button
              onClick={handleNextPage}
              disabled={!hasNextPage}
              whileHover={hasNextPage ? { scale: 1.05, y: -2 } : {}}
              whileTap={hasNextPage ? { scale: 0.95 } : {}}
              className={`relative flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                hasNextPage
                  ? "bg-[#0C1113] border border-gray-800/50 text-white hover:bg-[#0C1113]/80 hover:border-gray-700/50 hover:shadow-lg"
                  : "bg-gray-800 text-gray-500 cursor-not-allowed"
              }`}
            >
              <span className="absolute inset-0 bg-white opacity-0 group-hover:opacity-5 transition-opacity duration-200 rounded-lg" />
              Next
              <ChevronRight className="w-4 h-4" />
            </motion.button>
          </motion.div>
        )}
      </div>
    </div>
  );
}
